import * as O from "fp-ts/Option"
import { pipe } from "fp-ts/function"

// Chargement complet d'une liste paginée, borné : un écran qui doit voir
// toutes les lignes (participants d'une session, créneaux d'une session) lit
// les pages une à une jusqu'à la dernière, sans jamais dépasser `maxPages`.

/** Plafond par défaut : 10 pages de 100 lignes. */
export const MAX_FETCHED_PAGES = 10

export interface FetchedPage<T> {
  items: T[]
  /** Nombre total de pages annoncé par l'API. */
  totalPages: number
}

export interface AllPages<T> {
  items: T[]
  /** Faux quand le plafond de pages a coupé la liste. */
  complete: boolean
}

export function fetchAllPages<T>(
  fetchPage: (page: number) => Promise<FetchedPage<T>>,
  maxPages: number = MAX_FETCHED_PAGES,
): Promise<AllPages<T>> {
  const step = (page: number, acc: T[]): Promise<AllPages<T>> =>
    fetchPage(page).then(({ items, totalPages }) => {
      const all = acc.concat(items)
      if (page >= totalPages || items.length === 0) return { items: all, complete: true }
      if (page >= maxPages) return { items: all, complete: false }
      return step(page + 1, all)
    })
  return step(1, [])
}

/** Lignes d'une liste chargée par `fetchAllPages`, vide tant qu'elle se charge. */
export function loadedItems<T>(loaded: AllPages<T> | undefined): T[] {
  return pipe(
    O.fromNullable(loaded),
    O.match(
      (): T[] => [],
      (all) => all.items,
    ),
  )
}

/** Vrai quand le plafond de pages a coupé la liste chargée : l'écran doit le dire. */
export function isTruncated(loaded: AllPages<unknown> | undefined): boolean {
  return pipe(
    O.fromNullable(loaded),
    O.exists((all) => !all.complete),
  )
}
